import {
	Avatar,
	Button,
	Popup,
	Space,
	Image,
	Dialog,
	Collapse,
} from "antd-mobile";
import { createUseStyles } from "react-jss";
import { pxToRem } from "@/util/common";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/redux/store";
import { useEffect, useMemo, useState } from "react";
import { isDef } from "@/util/validate";
import useCustomTranslation from "@/hooks/useTranslation";
import { clearUserInfo } from "@/redux/reducers/authSlice";
import useRouter from "@/hooks/useRouter";
import i18n, {
	Language,
	supportedLanguages,
	changeLanguage,
	defaultLang,
	translateLanguage,
} from "@/locales";
import { gameColumns } from "../type";

const LanguageSelector: React.FC<{
	activeLanguage: Language | undefined;
	alternativeLanguages: Language[];
	languageActiveKey: string[];
	setLanguageActiveKey: (keys: string[]) => void;
	handleChangeLanguage: (lan: Language) => void;
}> = ({
	activeLanguage,
	alternativeLanguages,
	languageActiveKey,
	setLanguageActiveKey,
	handleChangeLanguage,
}) => {
	const classes = useStyles();

	return (
		<Collapse
			className={classes.customCollapse}
			activeKey={languageActiveKey}
			onChange={(keys) => {
				setLanguageActiveKey(keys);
			}}
			arrow={
				<Image
					src={require("@/assets/images/icons/arrow_up.png")}
					width={pxToRem(11)}
				/>
			}
		>
			<Collapse.Panel
				key="1"
				title={
					<div className={classes.languageTitle}>
						<span className="name">{activeLanguage?.name}</span>
					</div>
				}
			>
				{alternativeLanguages.map((item) => {
					return (
						<div
							key={item.val}
							className={classes.languageItem}
							onClick={() => {
								setLanguageActiveKey([""]);
								handleChangeLanguage(item);
							}}
						>
							{item.name}
						</div>
					);
				})}
			</Collapse.Panel>
		</Collapse>
	);
};

const UserCenterPopup: React.FC<{
	visible: boolean;
	adminShowed: boolean;
	gameColumnsList: gameColumns[];
	onClose?: () => void;
	onWalletClick?: () => void;
	onRewardClick?: () => void;
	onSetGameCompany?: (gameCompany: string) => void;
}> = ({
	visible,
	adminShowed,
	gameColumnsList,
	onClose,
	onWalletClick,
	onRewardClick,
	onSetGameCompany,
}) => {
	const { userInfo, nativeUser } = useSelector(
		(state: RootState) => state.auth
	);

	const telegram = window.Telegram?.WebApp;
	const isTelegram = !!telegram?.initData;

	const classes = useStyles();
	const { t } = useCustomTranslation();
	const dispatch: AppDispatch = useDispatch();
	const { customNavigate } = useRouter();
	const [activeLanguage, setActiveLanguage] = useState<Language>();
	const [languageActiveKey, setLanguageActiveKey] = useState<string[]>([
		defaultLang ?? "",
	]);
	const alternativeLanguages = useMemo(() => {
		let list = [...supportedLanguages];
		const index = list.findIndex((item) => item.val == activeLanguage?.val);
		if (isDef(index)) {
			list.splice(index, 1);
		}
		return list;
	}, [activeLanguage]);

	useEffect(() => {
		setTimeout(() => {
			const selectedLanVal = i18n.language;
			const language = supportedLanguages.find(
				(item) => item.val === selectedLanVal
			);
			setActiveLanguage(language);
		}, 500);
	}, []);

	const logout = async () => {
		const confirm = await Dialog.confirm({
			content: t("LogoutAlertContent"),
			cancelText: t("Cancel"),
			confirmText: t("Confirm"),
		});
		if (confirm) {
			dispatch(clearUserInfo());
			onClose?.();
			customNavigate("/login", { replace: true });
		}
	};

	const onNavigate = (page: string) => {
		if (!isDef(userInfo)) return;
		onClose?.();
		const outerParams = {
			nickname: userInfo.nickname,
			walletAddress: userInfo.walletAddress,
			inviteRemake: "",
		};
		customNavigate(
			"/" + page + "/" + userInfo.id + "/" + JSON.stringify(outerParams)
		);
	};

	const handleChangeLanguage = (lan: Language) => {
		setActiveLanguage(lan);
		changeLanguage(lan.val);
		menuItems.forEach((category) => {
			if (category.category === "Gaming") {
				category.items = gameColumnsList.map((item) => ({
					icon: item.logo,
					iconSize: 24,
					label: translateLanguage(item.categoryOtherDesc, lan.val),
					handler: () => {
						onSetGameCompany?.(item.gameCompany);
					},
				}));
			}
		});
	};

	interface CategoryItem {
		icon: string;
		iconSize: number;
		label: string | React.ReactNode;
		handler?: () => void;
	}
	type MenuCategory = {
		category?: string;
		items: CategoryItem[];
	};
	let funcList: CategoryItem[] = [];
	if (adminShowed) {
		/**
		 * funcList.push({
			icon: require("@/assets/images/sideBar/adminDash.png"),
			iconSize: 24,
			label: "AdminPanel",
			handler: () => {
				onClose?.();
				customNavigate("/vaultManage");
			},
		});
		*/
	}
	const menuItems: MenuCategory[] = [
		{
			items: [
				{
					icon: require("@/assets/images/sideBar/home.png"),
					iconSize: 28,
					label: "Home",
					handler: () => {
						onClose?.();
						customNavigate("/game");
					},
				},
			],
		},
		{
			category: "Gaming",
			items: gameColumnsList.map((item) => ({
				icon: item.logo,
				iconSize: 24,
				label: translateLanguage(item.categoryOtherDesc, i18n.language),
				handler: () => {
					onSetGameCompany?.(item.gameCompany);
				},
			})),
		},
		{
			category: "User",
			items: [
				{
					icon: require("@/assets/images/sideBar/personalData.png"),
					iconSize: 24,
					label: "PersonalData",
					handler: () => {
						onClose?.();
						customNavigate("/personalData");
					},
				},
				{
					icon: require("@/assets/images/sideBar/wallet.png"),
					iconSize: 24,
					label: "Wallet",
					handler: () => {
						onClose?.();
						customNavigate("/wallet/1");
					},
				},
				{
					icon: require("@/assets/images/sideBar/gameRecords.png"),
					iconSize: 24,
					label: "GameRecords",
					handler: () => {
						onClose?.();
						customNavigate("/gameRecordsNow");
					},
				},
			],
		},
		{
			category: "Setup",
			items: [
				...funcList,
				{
					icon: require("@/assets/images/sideBar/adminDash.png"),
					iconSize: 24,
					label: "RebateManagement",
					handler: () => {
						onClose?.();
						customNavigate("/cashbackManage");
					},
				},
				{
					icon: require("@/assets/images/sideBar/language.png"),
					iconSize: 24,
					label: (
						<LanguageSelector
							activeLanguage={activeLanguage}
							alternativeLanguages={alternativeLanguages}
							languageActiveKey={languageActiveKey}
							setLanguageActiveKey={setLanguageActiveKey}
							handleChangeLanguage={handleChangeLanguage}
						/>
					),
				},
			],
		},
	];

	return (
		<Popup
			visible={visible}
			onClose={onClose}
			position="left"
			closeOnMaskClick
			bodyStyle={{ width: "72vw" }}
			bodyClassName={classes.popupContent}
		>
			<div className={classes.main}>
				<div className={classes.moreWrap} onClick={onClose}>
					<Image
						src={require("@/assets/images/icons/more2.png")}
						width={pxToRem(18)}
						height={pxToRem(17)}
					/>
				</div>

				<div className={classes.userInfoWrap}>
					<Avatar
						src={
							nativeUser?.avatar ??
							require("@/assets/images/icons/Avatar.png")
						}
						style={{
							"--border-radius": "100%",
							"--size": pxToRem(46),
							border: "2px solid #3C4F86",
						}}
						fallback={
							<Image
								src={require("@/assets/images/icons/Avatar.png")}
								width={pxToRem(46)}
								height={pxToRem(46)}
							/>
						}
					/>
					<Space
						block
						direction="vertical"
						className={classes.nicknameWrap}
					>
						<strong>
							{nativeUser?.name ?? userInfo?.nickname}
						</strong>
						{nativeUser?.name ? (
							<span>{userInfo?.nickname}</span>
						) : null}
					</Space>
				</div>

				{menuItems.map((category, index) => (
					<dl className={classes.listWrap} key={index}>
						{category.category && <dt>{t(category.category)}</dt>}
						{category.items.map((item, itemIndex) => (
							<dd key={itemIndex} onClick={item.handler}>
								<Image
									src={item.icon}
									width={pxToRem(item.iconSize)}
									height={pxToRem(item.iconSize)}
									placeholder={null}
								/>
								<strong>
									{category.category === "Gaming"
										? item.label
										: typeof item.label === "string"
										? t(item.label)
										: item.label}
								</strong>
							</dd>
						))}
					</dl>
				))}
			</div>
			{!isTelegram && (
				<div className={classes.footer}>
					<Button className="logout" onClick={logout}>
						<Space style={{ "--gap-horizontal": pxToRem(15) }}>
							<Image
								className="icon"
								src={require("@/assets/images/icons/exit.png")}
							/>
							<span>{t("Logout")}</span>
						</Space>
					</Button>
				</div>
			)}
			<div
				style={{
					position: "fixed",
					left: "44%",
					bottom: pxToRem(6),
					textAlign: "center",
					fontSize: pxToRem(10),
					paddingTop: pxToRem(10),
				}}
			>
				<span>V1.0.1</span>
			</div>
		</Popup>
	);
};

export default UserCenterPopup;
const useStyles = createUseStyles({
	popupContent: {
		backgroundColor: "#1C1F28",
		color: "#fff",
		padding: {
			bottom: pxToRem(26),
		},
		display: "flex",
		flexDirection: "column",

		"& .btn": {
			width: "100%",
			height: pxToRem(50),
			"--border-radius": pxToRem(10),
			"--border-style": "none",
			"--text-color": "#FFFFFF",
			padding: "0",
		},
	},

	main: {
		flex: 1,
		padding: {
			left: pxToRem(20),
			right: pxToRem(20),
			top: pxToRem(84),
			bottom: pxToRem(60),
		},
		marginBottom: pxToRem(6),
		overflow: "auto",
		position: "relative",
	},

	moreWrap: {
		position: "absolute",
		left: pxToRem(20),
		top: pxToRem(17),
		border: "1px solid #2F3A62",
		borderRadius: "100%",
		width: pxToRem(40),
		height: pxToRem(40),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},

	userInfoWrap: {
		display: "flex",
		alignItems: "center",
		padding: {
			top: pxToRem(17),
			right: pxToRem(15),
			bottom: pxToRem(17),
			left: pxToRem(15),
		},
		marginBottom: pxToRem(7),
		borderRadius: pxToRem(10),
		overflow: "hidden",
		background:
			"radial-gradient(72.09% 80.86% at 100% 49.35%, #213980 0%, #1F2534 100%)",
	},

	listWrap: {
		fontFamily: "Noto Sans SC",
		"& .adm-list-body": {
			"--adm-color-background": "transparent",
		},

		"& dt": {
			fontSize: pxToRem(12),
			fontWeight: 400,
			lineHeight: pxToRem(18),
			padding: {
				top: pxToRem(7),
				bottom: pxToRem(7),
			},
		},
		"& dd": {
			display: "flex",
			padding: {
				top: pxToRem(7),
				bottom: pxToRem(7),
			},
			"& strong": {
				flex: 1,
				overflow: "hidden",
				minHeight: pxToRem(24),
				paddingLeft: pxToRem(14),
				fontSize: pxToRem(14),
				fontWeight: 500,
				margin: "auto 0",
			},
		},
	},

	info: {},

	nicknameWrap: {
		"--gap": 0,
		color: "#fff",
		flex: 1,
		overflow: "hidden",
		paddingLeft: pxToRem(15),
		"& strong": {
			fontWeight: 600,
			fontSize: pxToRem(14),
		},
		"& span": {
			fontSize: pxToRem(12),
		},
	},
	nickname: {},

	balanceWrap: {
		margin: "0 auto",
		height: pxToRem(40),
		borderRadius: pxToRem(20),
		border: "1px solid #2F3A62",
		paddingLeft: pxToRem(12),
		paddingRight: pxToRem(10),
		backgroundColor: "1E212B",
		display: "flex",
		alignItems: "center",
		alignSelf: "center",

		"& > strong": {
			marginLeft: pxToRem(6),
			fontSize: pxToRem(14),
			fontWeight: 600,
		},
	},

	actions: {
		"& .funcBtn": {
			fontWeight: 600,
			background: "linear-gradient(90deg, #14161E, #202558)",
		},
		"& .funcBtnContent": {
			display: "flex",
			alignItems: "center",
			"& .icon": {
				width: pxToRem(58),
				height: pxToRem(50),
				marginRight: pxToRem(28),
				flex: "none",
			},
			"& strong": {
				flex: 1,
				textAlign: "left",
			},
			"& .dot": {
				marginRight: pxToRem(18),
				width: pxToRem(12),
				height: pxToRem(12),
				backgroundColor: "#FF2B2B",
				border: "1px solid #2D3552",
				borderRadius: "100%",
				boxShadow: `0 ${pxToRem(4)} ${pxToRem(4)} rgba(0, 0, 0, 0.25)`,
			},
		},
		"& .categoryBtn": {
			fontWeight: 500,
			background: "#12141C",
			textAlign: "left",
			position: "relative",
			paddingLeft: pxToRem(31),

			"& .arrow": {
				position: "absolute",
				width: pxToRem(6),
				height: pxToRem(11),
				right: pxToRem(18),
				top: "50%",
				transform: "translate(0, -50%)",
			},
		},
	},

	footer: {
		flex: "none",
		borderTop: "1px solid #2F3A62",

		"& > .logout": {
			display: "flex",
			alignItems: "center",
			paddingLeft: pxToRem(24),
			fontWeight: 500,
			background: "#12141C",
			textAlign: "left",
			position: "relative",
			color: "#FF6161",

			width: "100%",
			height: pxToRem(88),
			"--border-style": "none",
			"--text-color": "#FFFFFF",
			padding: "0",

			"& .icon": {
				width: pxToRem(24),
				height: pxToRem(24),
			},
		},
	},

	languageTitle: {
		display: "flex",
		width: "100%",
		alignItems: "center",

		"& .name": {
			flex: 1,
			fontSize: pxToRem(14),
			fontWeight: 500,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
	},

	languageItem: {
		lineHeight: pxToRem(50),
		fontSize: pxToRem(14),
		fontWeight: 500,
		color: "#fff",
		paddingLeft: pxToRem(23),
		backgroundColor: "#0C0D14",
		borderBottom: "1px solid #2F3A62",
		"&:last-of-type": {
			borderBottom: "none",
		},
	},

	customCollapse: {
		"& .adm-list-body": {
			border: "none",
			"--padding-right": pxToRem(18),
			"--padding-left": 0,
			"--border-inner": "transparent",
			borderRadius: pxToRem(10),
		},
		"& .adm-collapse-panel-header .adm-list-item-content-main": {
			padding: {
				top: pxToRem(0),
				bottom: pxToRem(0),
			},
		},
		"& .adm-collapse-panel-content-inner .adm-list-item-content-main": {
			padding: {
				bottom: pxToRem(0),
			},
			borderRadius: pxToRem(10),
			overflow: "hidden",
			marginTop: pxToRem(20),
		},

		"& .adm-list-item:active:not(.adm-list-item-disabled)": {
			backgroundColor: "transparent",
		},
		"& .adm-list-item-content": {
			borderTop: 0,
		},
	},
});
